import { type FormEvent, useMemo, useState } from 'react';
import { renderSVG } from 'uqr';
import { authClient, authErrorMessage } from '../lib/auth-client';
import { useSession } from '../lib/session';
import { Alert, Button, buttonClass, Field, TextLink } from './ui';

type Step =
  | { kind: 'idle' }
  | { kind: 'password'; action: 'enable' | 'disable' | 'codes' }
  | { kind: 'scan'; totpURI: string; backupCodes: string[] }
  | { kind: 'codes'; backupCodes: string[] };

function secretFrom(totpURI: string): string {
  return new URL(totpURI).searchParams.get('secret') ?? '';
}

function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join('\n');
  return (
    <div className="space-y-4">
      <Alert tone="success">
        Save these backup codes somewhere safe. Each one works once if you lose your phone.
      </Alert>
      <ul className="grid grid-cols-2 gap-2 rounded-xl border border-line bg-field p-4 font-mono text-sm text-ink">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigator.clipboard?.writeText(text)}
        >
          Copy codes
        </Button>
        <a
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(`MOGZO backup codes\n\n${text}\n`)}`}
          download="mogzo-backup-codes.txt"
          className={buttonClass('secondary')}
        >
          Download
        </a>
      </div>
      <Button type="button" onClick={onDone}>
        I’ve saved them
      </Button>
    </div>
  );
}

/** Turn two-factor on (QR → code → backup codes), off, or get new backup codes. */
export function TwoFactorSettings({ enabled, required }: { enabled: boolean; required: boolean }) {
  const { refresh } = useSession();
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const qr = useMemo(
    () => (step.kind === 'scan' ? renderSVG(step.totpURI, { border: 2 }) : null),
    [step],
  );

  /**
   * A 401 means this browser's session ended, e.g. after the password was set or changed
   * (that signs the account out everywhere). Say so, instead of a message that reads like
   * a wrong password.
   */
  function fail(failed: { code?: string; message?: string; status?: number } | null) {
    if (failed?.status === 401) {
      setSignedOut(true);
      setStep({ kind: 'idle' });
      return;
    }
    setError(authErrorMessage(failed));
  }

  function reset() {
    setStep({ kind: 'idle' });
    setPassword('');
    setCode('');
    setError(null);
  }

  async function onPassword(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== 'password') return;
    setBusy(true);
    setError(null);
    if (step.action === 'enable') {
      const { data, error: failed } = await authClient.twoFactor.enable({ password });
      setBusy(false);
      if (failed || !data || !('totpURI' in data)) return fail(failed);
      setStep({ kind: 'scan', totpURI: data.totpURI, backupCodes: data.backupCodes });
    } else if (step.action === 'disable') {
      const { error: failed } = await authClient.twoFactor.disable({ password });
      setBusy(false);
      if (failed) return fail(failed);
      await refresh();
      reset();
    } else {
      const { data, error: failed } = await authClient.twoFactor.generateBackupCodes({ password });
      setBusy(false);
      if (failed || !data) return fail(failed);
      setStep({ kind: 'codes', backupCodes: data.backupCodes });
    }
    setPassword('');
  }

  async function onCode(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== 'scan') return;
    setBusy(true);
    setError(null);
    const { error: failed } = await authClient.twoFactor.verifyTotp({
      code: code.replace(/\s/g, ''),
    });
    setBusy(false);
    if (failed) return fail(failed);
    await refresh();
    setStep({ kind: 'codes', backupCodes: step.backupCodes });
  }

  return (
    <section
      aria-labelledby="two-factor-heading"
      className="rounded-2xl border border-line bg-card p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="two-factor-heading" className="text-lg font-bold text-ink">
            Two-factor authentication
          </h2>
          <p className="mt-1 text-sm text-muted">
            {enabled
              ? 'On. Signing in needs a code from your authenticator app.'
              : 'Off. Add a code from an authenticator app to every sign-in.'}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-field text-muted'}`}
        >
          {enabled ? 'On' : 'Off'}
        </span>
      </div>

      {required && !enabled && step.kind === 'idle' && (
        <div className="mt-4">
          <Alert tone="info">
            Your role requires two-factor. Turn it on to use developer or admin tools.
          </Alert>
        </div>
      )}

      <div className="mt-5 space-y-4">
        {signedOut && (
          <Alert>
            You’ve been signed out, for example because your password was just set or changed.{' '}
            <TextLink to="/sign-in?next=/account">Sign in again</TextLink> to continue.
          </Alert>
        )}
        {error && <Alert>{error}</Alert>}

        {step.kind === 'idle' &&
          (enabled ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep({ kind: 'password', action: 'codes' })}
              >
                New backup codes
              </Button>
              {!required && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setStep({ kind: 'password', action: 'disable' })}
                >
                  Turn off
                </Button>
              )}
            </div>
          ) : (
            <Button type="button" onClick={() => setStep({ kind: 'password', action: 'enable' })}>
              Turn on two-factor
            </Button>
          ))}

        {step.kind === 'password' && (
          <form onSubmit={onPassword} className="space-y-4">
            <Field
              label="Confirm your password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Button type="button" variant="secondary" onClick={reset}>
                Cancel
              </Button>
              <Button busy={busy} variant={step.action === 'disable' ? 'danger' : 'primary'}>
                Continue
              </Button>
            </div>
          </form>
        )}

        {step.kind === 'scan' && (
          <form onSubmit={onCode} className="space-y-4">
            <p className="text-sm text-ink">
              Scan this with an authenticator app (Google Authenticator, 1Password, Authy…), then
              enter the 6-digit code it shows.
            </p>
            {qr && (
              <img
                src={`data:image/svg+xml;utf8,${encodeURIComponent(qr)}`}
                alt="QR code for your authenticator app"
                className="mx-auto size-48 rounded-xl bg-white p-2"
              />
            )}
            <p className="text-center text-xs text-muted">
              Can’t scan? Enter this key:{' '}
              <code className="break-all font-mono text-ink">{secretFrom(step.totpURI)}</code>
            </p>
            <Field
              label="Code from the app"
              autoComplete="one-time-code"
              inputMode="numeric"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Button type="button" variant="secondary" onClick={reset}>
                Cancel
              </Button>
              <Button busy={busy}>Turn on</Button>
            </div>
          </form>
        )}

        {step.kind === 'codes' && <BackupCodes codes={step.backupCodes} onDone={reset} />}
      </div>
    </section>
  );
}
